/**
 * "What this changes" - limits that would fall below current usage, plus features that
 * would switch off. Advisory: the server does not block on these (unlike the site quota,
 * whose enforcement suspends sites), but the customer must see them BEFORE confirming.
 *
 * Storage and the AI quotas are PER SITE, so the server reports the worst-affected site -
 * on a multi-site account that is the one that hits the wall first.
 */
import { Info } from "lucide-react";
import { featureShortLabel, featureLabel, fill, type T } from "../i18n";
import type { ChangeImpacts, Locale } from "../types";
import { formatLimitValue } from "./lib";

export function ImpactList({
  impacts,
  locale,
  t,
  /** Keys already shown elsewhere (e.g. `sites.max` in the quota block) - don't repeat. */
  omitKeys = [],
}: {
  impacts: ChangeImpacts;
  locale: Locale;
  t: T;
  omitKeys?: string[];
}) {
  const rows = impacts.entitlements.filter((i) => !omitKeys.includes(i.key));
  if (rows.length === 0 && impacts.lostFeatures.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-1.5">
        <Info className="size-3.5 text-muted-foreground" />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          {t("impact.title")}
        </p>
      </div>
      <ul className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <li key={row.key} className="text-sm text-muted-foreground">
            {fill(t("impact.line"), {
              feature: featureShortLabel(row.key, locale),
              used: formatLimitValue(row.key, row.used, locale),
              limit: formatLimitValue(row.key, row.newLimit, locale),
            })}
            {row.siteName && <span className="text-muted-foreground/70"> · {fill(t("impact.onSite"), { site: row.siteName })}</span>}
          </li>
        ))}
        {impacts.lostFeatures.length > 0 && (
          <li className="text-sm text-muted-foreground">
            {fill(t("impact.lost"), { features: impacts.lostFeatures.map((k) => featureLabel(k, locale)).join(", ") })}
          </li>
        )}
      </ul>
      {rows.length > 0 && <p className="mt-2 text-xs text-muted-foreground/70">{t("impact.keepsWorking")}</p>}
    </div>
  );
}
